import {FC, createContext, useContext, useEffect, useState} from 'react'

import {WithChildren} from '_metronic/helpers'

import {PageManifest} from './pageContextTypes'

type PageContextStore = {
  publish: (token: symbol, manifest: PageManifest) => void
  clear: (token: symbol) => void
  /** The merged manifest of every page part currently mounted, or null. */
  read: () => PageManifest | null
}

function mergeIds(lists: Array<string[] | undefined>): string[] | undefined {
  const merged = [...new Set(lists.flatMap((list) => list ?? []))]
  return merged.length > 0 ? merged : undefined
}

/**
 * Held outside React state on purpose: the chat reads it once, when a message is sent, so
 * publishing never re-renders anything. Several parts of one page can publish at once (the
 * settings page its tabs, the section list inside it its sections) and are merged on read.
 */
export function createPageContextStore(): PageContextStore {
  const entries = new Map<symbol, PageManifest>()
  return {
    publish: (token, manifest) => {
      entries.set(token, manifest)
    },
    clear: (token) => {
      entries.delete(token)
    },
    read: () => {
      if (entries.size === 0) return null
      const manifests = [...entries.values()]
      const page = [...manifests].reverse().find((manifest) => manifest.page)?.page
      const visible = {
        tabs: mergeIds(manifests.map((manifest) => manifest.visible?.tabs)),
        sections: mergeIds(manifests.map((manifest) => manifest.visible?.sections)),
        actions: mergeIds(manifests.map((manifest) => manifest.visible?.actions)),
      }
      const visibleEntries = Object.entries(visible).filter(([, ids]) => ids)
      return {
        ...(page ? {page} : {}),
        ...(visibleEntries.length > 0 ? {visible: Object.fromEntries(visibleEntries)} : {}),
      }
    },
  }
}

const noopStore: PageContextStore = {publish: () => {}, clear: () => {}, read: () => null}

const PageContextStoreContext = createContext<PageContextStore>(noopStore)

/** Mounted above both the routes and the chatbot, so a page can publish and the chat can read. */
export const PageContextProvider: FC<WithChildren> = ({children}) => {
  const [store] = useState(createPageContextStore)
  return (
    <PageContextStoreContext.Provider value={store}>{children}</PageContextStoreContext.Provider>
  )
}

/**
 * Publishes this page's manifest while the component is mounted and clears it on unmount, the
 * way Metronic's `PageTitle` sets the toolbar title. Pass null to publish nothing. Ids and
 * booleans only — never display text or record data.
 */
export function usePublishPageContext(manifest: PageManifest | null): void {
  const store = useContext(PageContextStoreContext)
  const [token] = useState(() => Symbol('page-context'))
  // Compared by content: callers build the manifest inline on every render.
  const key = manifest ? JSON.stringify(manifest) : ''

  useEffect(() => {
    if (!key) return undefined
    store.publish(token, JSON.parse(key) as PageManifest)
    return () => store.clear(token)
  }, [key, store, token])
}

/** Reads the current manifest on demand (at send time); stable across renders. */
export function usePageContextReader(): () => PageManifest | null {
  return useContext(PageContextStoreContext).read
}
